/**
 * Viewer Store - Global state management for Forge Viewer
 * Uses Zustand for state management
 */

import { create } from 'zustand';
import { config } from '@/api/core/config';
import type { ClearanceVolumeResult, ClearanceVolumeConfig } from '@/types/clearanceVolume';
import { getDefaultModelConfig, getModelConfig } from '@/config/models';

declare const THREE: any;

interface ViewerState {
  // Viewer instance
  viewer: Autodesk.Viewing.GuiViewer3D | null;

  // Model state
  selectedModelId: string;
  modelUrn: string;
  isModelLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  shouldAutoLoad: boolean;

  // Selection state
  selectedDbIds: number[];

  // Jurisdiction override (e.g., user picks Texas vs Florida for Dutch Bros)
  selectedJurisdiction: string | null;

  // Clearance Volume state
  clearanceVolumes: Map<number, THREE.Object3D>;
  clearanceResults: Map<number, ClearanceVolumeResult>;
  clearanceConfig: ClearanceVolumeConfig | null;

  // Actions
  setViewer: (viewer: Autodesk.Viewing.GuiViewer3D | null) => void;
  setSelectedModelId: (id: string) => void;
  setModelUrn: (urn: string) => void;
  setModelLoaded: (loaded: boolean) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedDbIds: (dbIds: number[]) => void;
  setShouldAutoLoad: (shouldAutoLoad: boolean) => void;
  setSelectedJurisdiction: (jurisdiction: string | null) => void;

  // Viewer operations
  loadModel: (urn: string) => void;
  clearSelection: () => void;
  selectElements: (dbIds: number[]) => void;
  isolateElements: (dbIds: number[]) => void;
  fitToView: (dbIds?: number[]) => void;
  resetView: () => void;
  reset: () => void;

  // Clearance Volume actions
  setClearanceVolume: (dbId: number, mesh: THREE.Object3D) => void;
  removeClearanceVolume: (dbId: number) => void;
  clearAllClearanceVolumes: () => void;
  setClearanceResult: (dbId: number, result: ClearanceVolumeResult) => void;
  setClearanceConfig: (config: ClearanceVolumeConfig) => void;
}

export const useViewerStore = create<ViewerState>((set, get) => ({
  // Initial state
  viewer: null,
  selectedModelId: getDefaultModelConfig().id,
  modelUrn: '',
  isModelLoaded: false,
  isLoading: false,
  error: null,
  shouldAutoLoad: false,
  selectedDbIds: [],
  selectedJurisdiction: null,

  // Clearance Volume state
  clearanceVolumes: new Map(),
  clearanceResults: new Map(),
  clearanceConfig: null,

  // Actions
  setViewer: (viewer) => set({ viewer }),
  setSelectedModelId: (id) => {
    const modelConfig = getModelConfig(id);
    if (modelConfig) {
      set({
        selectedModelId: id,
        isModelLoaded: false,
        selectedDbIds: [],
        selectedJurisdiction: null,
        clearanceVolumes: new Map(),
        clearanceResults: new Map(),
      });
      get().loadModel(modelConfig.urn);
    }
  },
  setModelUrn: (urn) => set({ modelUrn: urn, shouldAutoLoad: false }),
  setModelLoaded: (loaded) => set({ isModelLoaded: loaded }),
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setSelectedDbIds: (dbIds) => set({ selectedDbIds: dbIds }),
  setShouldAutoLoad: (shouldAutoLoad) => set({ shouldAutoLoad }),
  setSelectedJurisdiction: (jurisdiction) => set({ selectedJurisdiction: jurisdiction }),

  // Load model - explicitly set shouldAutoLoad to true
  loadModel: (urn) => {
    set({ modelUrn: urn, isLoading: true, error: null, shouldAutoLoad: true });
  },

  // Reset all viewer state
  reset: () => {
    const { viewer } = get();
    if (viewer) {
      try {
        viewer.finish();
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
    set({
      viewer: null,
      selectedModelId: getDefaultModelConfig().id,
      modelUrn: '',
      isModelLoaded: false,
      isLoading: false,
      error: null,
      shouldAutoLoad: false,
      selectedDbIds: [],
      clearanceVolumes: new Map(),
      clearanceResults: new Map(),
      clearanceConfig: null,
    });
  },

  // Clear selection
  clearSelection: () => {
    const { viewer } = get();
    if (viewer) {
      viewer.clearSelection();
      set({ selectedDbIds: [] });
    }
  },

  // Select elements
  selectElements: (dbIds) => {
    const { viewer } = get();
    if (viewer) {
      viewer.select(dbIds);
      set({ selectedDbIds: dbIds });
    }
  },

  // Isolate elements
  isolateElements: (dbIds) => {
    const { viewer } = get();
    if (viewer) {
      viewer.isolate(dbIds);
    }
  },

  // Fit to view
  fitToView: (dbIds) => {
    const { viewer } = get();
    if (viewer) {
      viewer.fitToView(dbIds);
    }
  },

  // Reset view
  resetView: () => {
    const { viewer } = get();
    if (viewer) {
      viewer.isolate([]);
      viewer.fitToView();
    }
  },

  // Clearance Volume actions
  setClearanceVolume: (dbId, mesh) => {
    set((state) => {
      const volumes = new Map(state.clearanceVolumes);
      volumes.set(dbId, mesh);
      return { clearanceVolumes: volumes };
    });
  },

  removeClearanceVolume: (dbId) => {
    set((state) => {
      const volumes = new Map(state.clearanceVolumes);
      volumes.delete(dbId);
      return { clearanceVolumes: volumes };
    });
  },

  clearAllClearanceVolumes: () => {
    set({ clearanceVolumes: new Map(), clearanceResults: new Map() });
  },

  setClearanceResult: (dbId, result) => {
    set((state) => {
      const results = new Map(state.clearanceResults);
      results.set(dbId, result);
      return { clearanceResults: results };
    });
  },

  setClearanceConfig: (config) => {
    set({ clearanceConfig: config });
  }
}));
